import classNames from 'classnames';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { TooltipContainerContext } from 'components/common-form/contexts/TooltipContainerContext';
import { InvalidCount } from 'components/common-form/hooks/invalid-fields/model';
import { useCurrentBlockInvalidFields } from 'components/common-form/hooks/invalid-fields/useCurrentBlockInvalidFields';
import { useCurrentBlockModifiedCount } from 'components/common-form/hooks/patched-fields/useCurrentBlockModifiedCount';
import { useBlockMetadata } from 'components/common-form/hooks/useBlockMetadata';
import { useProductData } from 'components/common-form/hooks/useData';
import { InfoIconButton } from 'components/shared/InfoIconButton';
import { InfoTooltip } from 'components/shared/InfoTooltip';
import { useHelpCenterFeature } from 'hooks/use-help-center-feature';
import { useIsAnyRouteActive } from 'hooks/use-is-any-route-active';
import type { IntlKey } from 'locales';
import type { BlockMetadata } from 'models/metadata/block.definition';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { camelCaseToWords } from 'utils/string';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';
import { CountLabel } from './components/CountLabel';
import { FieldCountBadges } from './components/FieldCountBadges';
import { ChildNode, FieldsList } from './FieldsList';

const StyledDiv = styled.div`
  &.block-dimmed {
    cursor: not-allowed;

    .card {
      pointer-events: none;
    }
  }
`;

export const DefaultBlock: FC<ProductNodeBuilderProps> = ({
  tabId,
  level,
  node: { id, children, width, form, direction },
  workspaceType,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
}) => {
  const metadata = useBlockMetadata(id)!;
  const { formatMessage } = useIntl();
  const blockLabel = useMemo(() => {
    switch (workspaceType) {
      case 'dynamic':
        return camelCaseToWords(id);
      default:
        return formatMessage({ id: `form.${id}` as any, defaultMessage: camelCaseToWords(id) });
    }
  }, [formatMessage, id, workspaceType]);
  const invalidCount = useCurrentBlockInvalidFields(children);
  const modifiedCount = useCurrentBlockModifiedCount(children);

  const isNestedBlock = level > 1;
  const hasNestedBlock = children?.some(c => (c.children?.length ?? 0) > 0);

  return (
    <StyledDiv
      data-e2e={`static-form-block-${id}`}
      id={id}
      className={classNames('block', {
        'w-100': isNestedBlock || metadata.width === 'full',
        'block-has-nested': hasNestedBlock,
        'opacity-50 block-dimmed': metadata.isDimmed,
      })}
    >
      <TooltipContainerContext.Provider value={id}>
        <div
          className={classNames('card bg-lvl1 h-100', {
            ['shadow-sm']: !isNestedBlock,
            ['card-bordered my-2']: isNestedBlock,
          })}
        >
          <CardHeader
            invalidCount={invalidCount}
            modifiedCount={modifiedCount}
            metadata={metadata}
            label={blockLabel}
            id={id}
          />

          <div className="card-body">
            <div className="card-text">
              <FormOrientationContext.Provider value={form ?? 'horizontal'}>
                <FieldsList
                  direction={direction ?? 'leftToRight'}
                  nodes={children as any}
                  blockWidth={width ?? 'full'}
                  renderChild={(childNode, defaultWidth) => (
                    <ChildNode
                      key={childNode.id}
                      tabId={tabId}
                      node={childNode}
                      level={level}
                      defaultWidth={defaultWidth}
                      workspaceType={workspaceType}
                      quoteWorkspaceVersion={quoteWorkspaceVersion}
                      batchWorkspaceVersion={batchWorkspaceVersion}
                      mustForceAdvancedMode={mustForceAdvancedMode}
                    />
                  )}
                />
              </FormOrientationContext.Provider>
            </div>
          </div>
        </div>
      </TooltipContainerContext.Provider>
    </StyledDiv>
  );
};
DefaultBlock.displayName = 'DefaultBlock';

type CardHeaderProps = {
  id: string;
  metadata: Readonly<BlockMetadata>;
  label: string;
  invalidCount: InvalidCount;
  modifiedCount: number;
};

function CardHeader({ metadata, label, invalidCount, modifiedCount, id }: CardHeaderProps) {
  const isStaticOrDynamicScreen = useIsAnyRouteActive('/static', '/dynamic');

  if (isStaticOrDynamicScreen) {
    return (
      <StaticCardHeader
        metadata={metadata}
        invalidCount={invalidCount}
        modifiedCount={modifiedCount}
        label={label}
        id={id}
      />
    );
  }

  return (
    <BaseCardHeader
      metadata={metadata}
      invalidCount={invalidCount}
      modifiedCount={modifiedCount}
      label={label}
      id={id}
    />
  );
}

function BaseCardHeader({ metadata, label, invalidCount, modifiedCount, id }: CardHeaderProps) {
  const isFeatureMetadataType = metadata?.type === 'Feature';

  return (
    <div className="card-header">
      <h5 className="card-title">
        {label}
        {metadata?.type === 'DefaultWithCount' && <CountLabel count={metadata.count} labelId={metadata.countLabelId} />}
        <FieldCountBadges id={id} value={invalidCount} modifiedCount={modifiedCount} />
        {isFeatureMetadataType && (
          <InfoTooltip id={`info-feature-${metadata.id}`} tooltipKey={metadata.tooltipKey as IntlKey} />
        )}
      </h5>
    </div>
  );
}

function StaticCardHeader({ metadata, label, invalidCount, modifiedCount, id }: CardHeaderProps) {
  const { productSubtype, productType } = useProductData();
  const { exists, open } = useHelpCenterFeature({ name: metadata.id, productSubtype, productType });

  return (
    <div className="card-header">
      <h5 className="card-title" data-help-center-name={metadata.id}>
        {label}
        {metadata?.type === 'DefaultWithCount' && <CountLabel count={metadata.count} labelId={metadata.countLabelId} />}
        <FieldCountBadges id={id} value={invalidCount} modifiedCount={modifiedCount} />
        {exists && <InfoIconButton size="md" onClick={open} />}
      </h5>
    </div>
  );
}
